import { SelectDropdown } from "@dash/select";
import { useDeviceType } from "@dash/mq";
import { useClientManager } from "@megaron/react-clients";
import { useQueryClient } from "react-query";

import {
  ticketStatuses,
  TicketListItemDto,
  TicketStatus,
} from "@megaron/support-contracts";

const statusLabels: Record<TicketStatus, string> = {
  open: "Otwarty",
  resolved: "Zrealizowany",
  closed: "Zamknięty",
};

function isTicketStatus(value: string): value is TicketStatus {
  return value === "open" || value === "resolved" || value === "closed";
}

function buildUpdateBody(item: TicketListItemDto, status: TicketStatus) {
  return {
    id: item.id,
    subject: item.subject,
    status,
    priority: item.priority ?? null,
    category: item.category ?? null,
    tags: item.tags,
    crmCustomerUuid: null,
  };
}

type Props = {
  item: TicketListItemDto;
  compact?: boolean;
};

const statusOptions = ticketStatuses.map((status) => ({
  value: status,
  label: statusLabels[status],
}));

export function SupportTicketListStatusSelect({ item, compact = false }: Props) {
  const { isMobile } = useDeviceType();
  const queryClient = useQueryClient();
  const updateMutation = useClientManager("support").updateTicket().useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support", "ticketsList"] });
      queryClient.invalidateQueries({ queryKey: ["support", "getTicket"] });
    },
  });

  const handleChange = (raw: string) => {
    if (!isTicketStatus(raw) || raw === item.status) return;
    updateMutation.mutate(buildUpdateBody(item, raw));
  };

  return (
    <div
      css={
        compact
          ? {
              width: "100%",
              minWidth: "4.75rem",
              maxWidth: isMobile ? "100%" : "7rem",
              "& button": {
                padding: "0.25rem 0.375rem",
                fontSize: "0.8125rem",
                gap: "0.25rem",
              },
              "& button span:first-of-type": {
                fontSize: "0.8125rem",
              },
              "& svg": {
                width: "16px",
                height: "16px",
              },
            }
          : {
              width: "100%",
              minWidth: "7rem",
              maxWidth: isMobile ? "100%" : "10rem",
            }
      }
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <SelectDropdown
        value={isTicketStatus(item.status) ? item.status : undefined}
        options={statusOptions}
        onChange={handleChange}
        isDisabled={updateMutation.isLoading}
      />
    </div>
  );
}
